<template>
  <aside class="left-nav">
    <div
      class="logo-wrap"
      role="button"
      tabindex="0"
      title="返回首页"
      @click="$emit('navigate', '/home')"
      @keydown.enter="$emit('navigate', '/home')"
    >
      <img v-if="resolvedLogoImage" class="logo-image" :src="resolvedLogoImage" alt="logo" />
      <span v-else>{{ resolvedLogoTitle }}</span>
    </div>
    <div class="menu-list">
      <button
        v-for="item in menuItems"
        :key="item.path"
        class="menu-btn menu-btn--stacked"
        :class="{ active: isActive(item.path) }"
        @click="$emit('navigate', item.path)"
      >
        <span class="menu-btn-icon" aria-hidden="true" v-html="navIconSvg(item.icon)" />
        <span class="menu-btn-text">{{ item.label }}</span>
      </button>
    </div>

    <div class="bottom-actions">
      <div ref="serviceWrap" class="service-wrap">
        <button
          class="menu-btn menu-btn--stacked"
          type="button"
          @click.stop="toggleQrPanel"
        >
          <span class="menu-btn-icon" aria-hidden="true" v-html="navIconSvg('service')" />
          <span class="menu-btn-text">客服</span>
        </button>
        <div v-if="showQr" class="qr-panel">
          <img v-if="resolvedServiceQr" class="qr-image" :src="resolvedServiceQr" alt="客服二维码" />
          <div v-else class="qr-mock">客服二维码</div>
        </div>
      </div>

      <template v-if="isLogin">
        <button class="menu-btn menu-btn--stacked" @click="$emit('open-points')">
          <span class="menu-btn-icon" aria-hidden="true" v-html="navIconSvg('points')" />
          <span class="menu-btn-text">积分 {{ pointsBalance }}</span>
        </button>
      </template>

      <div class="user-panel">
        <button v-if="!isLogin" class="login-btn" @click="$emit('open-login')">
          登录
        </button>
        <div v-else ref="userAvatarWrap" class="user-avatar-wrap" @click.stop="toggleUserCard">
          <img
            v-if="resolvedAvatar"
            class="avatar"
            :src="resolvedAvatar"
            alt="avatar"
            @error="handleAvatarError"
          />
          <div v-else class="avatar avatar-placeholder">?</div>
          <div v-if="showUserCard" class="user-card" @click.stop>
            <div class="user-card-header">
              <img v-if="resolvedAvatar" class="avatar" :src="resolvedAvatar" alt="avatar" @error="handleAvatarError" />
              <div v-else class="avatar avatar-placeholder">?</div>
              <div class="user-card-info">
                <div class="user-id">UID：{{ userIdText }}</div>
                <div class="user-mobile">账号：{{ userMobileText }}</div>
                <button type="button" class="create-btn user-recharge-btn" @click="onRechargeClick">充值积分</button>
              </div>
            </div>
            <div class="user-card-actions">
              <button class="user-menu-btn vip-menu-btn" @click.prevent="goVip">
                <span class="user-menu-icon">💎</span>
                <span>会员中心</span>
              </button>
              <button class="user-menu-btn" @click.prevent="goOrders">
                <span class="user-menu-icon">📦</span>
                <span>我的订单</span>
              </button>
              <button class="user-menu-btn danger" @click.prevent="handleLogoutClick">
                <span class="user-menu-icon">⎋</span>
                <span>退出登录</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  name: "LeftNav",
  props: {
    pointsBalance: { type: Number, default: 0 },
    isLogin: { type: Boolean, default: false },
    avatar: { type: String, default: "" },
    defaultAvatar: { type: String, default: "" },
    serviceQr: { type: String, default: "" },
    logoTitle: { type: String, default: "" },
    logoImage: { type: String, default: "" },
    userId: { type: [Number, String], default: "" },
    userMobile: { type: String, default: "" }
  },
  data() {
    return {
      showQr: false,
      showUserCard: false,
      avatarBroken: false,
      serviceQrBroken: false,
      menuItems: [
        { label: "工作流", path: "/home", icon: "workflow" },
        { label: "视频", path: "/quick-video", icon: "video" },
        { label: "资产", path: "/assets", icon: "assets" }
      ]
    };
  },
  computed: {
    resolvedAvatar() {
      const userAvatar = this.normalizeMediaUrl(this.avatar);
      if (userAvatar && !this.avatarBroken) return userAvatar;
      const fallback = this.normalizeMediaUrl(this.defaultAvatar);
      if (fallback && !this.avatarBroken) return fallback;
      return "";
    },
    resolvedServiceQr() {
      const s = this.normalizeMediaUrl(this.serviceQr);
      if (!s || this.serviceQrBroken) return "";
      return s;
    },
    resolvedLogoTitle() {
      const s = String(this.logoTitle || "").trim();
      return s || "漫剧";
    },
    resolvedLogoImage() {
      return this.normalizeMediaUrl(this.logoImage);
    },
    userIdText() {
      const v = String(this.userId || "").trim();
      return v || "--";
    },
    userMobileText() {
      const v = String(this.userMobile || "").trim();
      return v || "--";
    }
  },
  methods: {
    normalizeMediaUrl(src) {
      const s = String(src || "").trim();
      if (!s) return "";
      if (/^https?:\/\//i.test(s)) return s;
      return s.startsWith("/") ? s : `/${s}`;
    },
    navIconSvg(name) {
      const svgWrap = body =>
        `<svg class="nav-icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">${body}</svg>`;
      const icons = {
        video: svgWrap(
          '<rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/><path d="M10 10.5v5l5.5-2.5L10 10.5z" fill="currentColor"/>'
        ),
        workflow: svgWrap(
          '<rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/><rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/><rect x="8.5" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.8"/><path d="M10 10v1.5M17 10v4.5M12 14h2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>'
        ),
        assets: svgWrap(
          '<rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/><circle cx="8.5" cy="10" r="1.5" fill="currentColor"/><path d="M21 17l-6-6-4 4-3-3-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>'
        ),
        service: svgWrap(
          '<path d="M4 6.5A3.5 3.5 0 017.5 3h9A3.5 3.5 0 0120 6.5v7A3.5 3.5 0 0116.5 17H10l-5 3.5V6.5z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M8 9.5h8M8 12.5h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>'
        ),
        points: svgWrap(
          '<circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.8"/><path d="M12 6.8l1.35 4.1h4.4l-3.55 2.6 1.35 4.2L12 14.9l-3.55 2.7 1.35-4.2-3.55-2.6h4.4L12 6.8z" fill="currentColor"/>'
        )
      };
      return icons[name] || "";
    },
    toggleQrPanel() {
      this.showQr = !this.showQr;
      if (this.showQr) this.showUserCard = false;
      this.$nextTick(() => this.syncOutsideCloseListener());
    },
    toggleUserCard() {
      this.showUserCard = !this.showUserCard;
      if (this.showUserCard) this.showQr = false;
      this.$nextTick(() => this.syncOutsideCloseListener());
    },
    onRechargeClick() {
      this.showUserCard = false;
      this.$emit("open-recharge");
    },
    goVip() {
      this.showUserCard = false;
      if (this.$router) {
        this.$router.push("/vip").catch(() => {});
      }
      this.$emit("navigate", "/vip");
      setTimeout(() => {
        if (this.$route && this.$route.path !== "/vip") {
          window.location.hash = "#/vip";
        }
      }, 60);
    },
    goOrders() {
      this.showUserCard = false;
      if (this.$router) {
        this.$router.push("/orders").catch(() => {});
      }
      this.$emit("navigate", "/orders");
      // 兜底：若某些情况下事件链/守卫拦截了 push，强制 hash 跳转到订单页
      setTimeout(() => {
        if (this.$route && this.$route.path !== "/orders") {
          window.location.hash = "#/orders";
        }
      }, 60);
    },
    handleLogoutClick() {
      this.showUserCard = false;
      this.$emit("logout");
    },
    isActive(path) {
      return this.$route.path === path;
    },
    handleAvatarError(e) {
      this.avatarBroken = true;
      if (e && e.target) e.target.style.display = "none";
    },
    onDocumentClick(e) {
      const target = e.target;
      const serviceEl = this.$refs.serviceWrap;
      const userEl = this.$refs.userAvatarWrap;
      if (serviceEl && serviceEl.contains(target)) return;
      if (userEl && userEl.contains(target)) return;
      this.showQr = false;
      this.showUserCard = false;
    },
    syncOutsideCloseListener() {
      const need = this.showQr || this.showUserCard;
      if (need && !this._outsideCloseBound) {
        document.addEventListener("click", this.onDocumentClick);
        this._outsideCloseBound = true;
      } else if (!need && this._outsideCloseBound) {
        document.removeEventListener("click", this.onDocumentClick);
        this._outsideCloseBound = false;
      }
    }
  },
  mounted() {
    this.syncOutsideCloseListener();
  },
  beforeDestroy() {
    if (this._outsideCloseBound) {
      document.removeEventListener("click", this.onDocumentClick);
      this._outsideCloseBound = false;
    }
  },
  watch: {
    avatar() {
      this.avatarBroken = false;
    },
    defaultAvatar() {
      this.avatarBroken = false;
    },
    serviceQr() {
      this.serviceQrBroken = false;
    },
    showQr() {
      this.syncOutsideCloseListener();
    },
    showUserCard() {
      this.syncOutsideCloseListener();
    }
  }
};
</script>

<style scoped>
.left-nav {
  position: fixed;
  left: 0;
  top: 0;
  width: 88px;
  height: 100vh;
  background: #1d1d1d;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px 8px;
  border-right: 1px solid #333333;
  z-index: 9;
}

.logo-wrap {
  width: 54px;
  height: 54px;
  border-radius: 10px;
  background: #333333;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s;
}

.logo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  display: block;
}

.logo-wrap:hover {
  background: #3a3a3a;
}

.logo-wrap:focus,
.logo-wrap:focus-visible,
.logo-wrap:active {
  outline: none;
}

.menu-list,
.bottom-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.menu-list {
  margin-top: 12px;
}

.bottom-actions {
  margin-top: auto;
  margin-bottom: 8px;
}

.menu-btn,
.create-btn,
.login-btn,
.tiny-btn {
  border: 0;
  background: #333333;
  color: #e9e9e9;
  border-radius: 8px;
  height: 34px;
  width: 66px;
  cursor: pointer;
  font-size: 12px;
}

.menu-btn--stacked {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: auto;
  min-height: 52px;
  padding: 6px 4px;
}

.menu-btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: #e9e9e9;
}

.menu-btn-icon ::v-deep .nav-icon-svg {
  width: 22px;
  height: 22px;
  display: block;
}

.menu-btn.active .menu-btn-icon {
  color: #19b06b;
}

.menu-btn-text {
  font-size: 11px;
  line-height: 1.2;
  text-align: center;
}

.menu-btn.active {
  outline: 1px solid #19b06b;
}

.create-btn,
.login-btn {
  background: #19b06b;
  color: #ffffff;
}

.user-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  position: relative;
}

.user-avatar-wrap {
  position: relative;
  cursor: pointer;
}

.user-recharge-btn {
  margin-top: 8px;
  width: 100%;
  height: 32px;
  font-size: 12px;
}

.avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #444444;
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
}

.tiny-btn {
  height: 24px;
  font-size: 11px;
}

.tiny-btn.danger {
  background: #773131;
}

.service-wrap {
  position: relative;
}

/* 不再向右「撑宽」命中区：原先 padding-right + 负 margin 会把热区伸进主内容区，鼠标还没碰到按钮就会触发悬停 */

.qr-panel {
  position: absolute;
  left: 80px;
  top: 0;
  width: 124px;
  background: #333333;
  border: 1px solid #444;
  border-radius: 10px;
  padding: 10px;
  z-index: 20;
  /* 向左略重叠按钮，减少按钮与面板之间的缝隙导致误触 mouseleave */
  margin-left: 8px;
}

.qr-mock {
  width: 100%;
  height: 102px;
  border-radius: 8px;
  background: #3a3a3a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.qr-image {
  width: 100%;
  height: 102px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.user-card {
  position: absolute;
  left: 80px;
  bottom: 0;
  width: 220px;
  background: #333333;
  border: 1px solid #444;
  border-radius: 10px;
  padding: 10px;
  z-index: 20;
  margin-left: -8px;
}

.user-card-header {
  display: flex;
  gap: 8px;
  align-items: center;
}

.user-card-info {
  min-width: 0;
  color: #e6e6e6;
  font-size: 12px;
}

.user-id,
.user-mobile {
  line-height: 1.4;
  word-break: break-all;
}

.user-card-actions {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.user-menu-btn {
  width: 100%;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #2f2f2f;
  color: #e9e9e9;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 0 10px;
  cursor: pointer;
  font-size: 12px;
}

.user-menu-btn.danger {
  background: #773131;
}

.user-menu-btn.vip-menu-btn {
  background: #3d3520;
  color: #f5e6b8;
}

.user-menu-icon {
  width: 14px;
  text-align: center;
  opacity: 0.95;
}

@media (max-width: 768px) {
  .menu-btn,
  .create-btn,
  .login-btn {
    width: 72px;
    font-size: 11px;
    padding: 0 4px;
    white-space: normal;
    line-height: 1.2;
    height: auto;
    min-height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .menu-btn--stacked {
    min-height: 52px;
    padding: 6px 4px;
  }

  .create-btn {
    font-size: 10px;
  }

  .user-card {
    width: min(220px, calc(100vw - 100px));
  }
}
</style>
